import { PlaidLinkOnSuccessMetadata } from 'react-plaid-link';
import { IAbTestData } from './store/createInitializeApiSlice';
import { ContextTypes } from './constants/pages';
import { IRecurringPaymentData } from './pages/BillPay/types';
import { LiliPlanType } from './flows/planUpgrade/constants';
import { FeatureAccess } from './pages/AccountSettings/components/rolesAndPermissions/PermissionsList';
import { InviteStatus } from './pages/AccountantOnboarding/hooks/mutateInviteStatus';
import { LimitedFeatureType } from './services/appAccess.service';

export type mfaType = 'SMS' | 'EMAIL' | 'VOICE';

export type mfaStatus = 'MFA_CHALLENGE' | 'SUCCESS';

export type moveMoneyActionTypes = 'WIRE_OUT';

export interface IMoveMoneyReviewResponse {
  customerId: string;
  mfaDestination: string;
  mfaType: mfaType;
  mfaTypesOptions: ('SMS' | 'VOICE')[];
  status: mfaStatus;
  actionData: string;
  actionType: moveMoneyActionTypes;
}

export interface IAddressObject {
  street_address: string;
  apt_suite: string;
  city: string;
  state: string;
  zip_code: string;
}

export interface IAddressValidationObject {
  street_address: boolean;
  apt_suite: boolean;
  state: boolean;
  city: boolean;
  zip_code: boolean;
  hasNoServerErrors: boolean;
}

interface IInternalServerError {
  code: number;
  codeName: string;
  message: string;
  localizedMessage: string;
}

export interface IServerError {
  response: {
    data: {
      errors: IInternalServerError[];
    };
  };
}

export interface ICard {
  cardId: string;
  status: string;
  locked: boolean;
  shippingStatus: string;
  shippingDate?: Date;
  requireActivation: boolean;
  digital: boolean;
  reissueCanBeActivated: boolean;
  prettyStatus?: number;
  createTime?: Date | string;
}

export interface IWelcomeFormData {
  email: string;
  password: string;
}

export interface IWelcomeHandleInput {
  key: string;
  value: string;
  validation: RegExp | string;
  optional?: boolean;
  internalError?: string;
}

export interface ILoginDetails {
  loginName: string;
  password: string;
  clientId: string | undefined;
  fingerPrint?: string | null;
}

export enum RoleType {
  ACCOUNTANT = 'ACCOUNTANT',
  ADMIN = 'ADMIN',
  UBO = 'UBO',
}

export interface ILoginResponse {
  customerId: string; // is not client_id, but uses as an env indicator
  status: string;
  loginResponseCustomData: {
    mfaDestination: mfaType;
    needToConvert: boolean;
    stateToken: string;
  };
  mfaDestination: string;
  mfaType: mfaType;
  allowEmailResend: boolean;
  allowSmsResend: boolean;
  allowVoiceResend: boolean;
  firstLogin: boolean;
  authData: {
    accessToken: string;
    refreshToken: string;
  };
  accessToken?: string;
  filteredInitializeArr?: IAbTestData[];
  authorizedUserId?: string;
  authorizedUserType?: RoleType;
    initialPassword?: string;

}

export interface ILoginRadioButtons {
  ID: string;
  TITLE: string;
  BUTTON_TITLE: string;
  TYPE: mfaType;
  CHECKED?: boolean;
}

export interface IVerifyData {
  customerId: string;
  answer?: string;
  clientId?: string;
  customData: {
    mfaDestination: mfaType;
    needToConvert: boolean;
    stateToken: string;
  };
  mfaType: mfaType;
  rememberMe: boolean;
  authUserType?: string;
  authUserId?: string;
}

export interface IInputContent {
  value: string;
  validation: RegExp;
  optional?: boolean;
  internalError?: boolean;
}

export interface IError {
  code: number;
  codeName: string;
  localizedMessage: string;
  message: string;
  data?: {
    url?: string;
  };
}

export interface IServerErrorResponse {
  response: {
    data: {
      errors: IError[];
      httpStatus: number;
      reasonPhrase: string;
    };
    status?: number;
  };
}

export interface IFlattenServerErrorResponse {
  reasonPhrase: string;
  httpStatus: number;
  code: number;
  codeName: string;
  localizedMessage: string;
  message: string;
  status: number | undefined;
}

export interface IBlink {
  name: string;
  path: string;
  id: string;
  requiredType?: string;
  featureFlagId?: string;
  upgradeToUseFeature?: boolean;
  actionFlag?: string;
  callback?: (data?: any) => void;
  analyticsCb?: () => void;
  shouldValidateSubLink?: { func: (type: string, userSummary?: any, actionFlag?: string) => boolean };
  shouldHide?: boolean;
  disabledForRoles?: RoleType[];
  limitedFeatureType?: LimitedFeatureType;
  disabled?: boolean;
}

export interface ISidebarLink {
  id: string;
  linkName: string;
  path: string;
  icon?: string;
  iconSelected?: string;
  subLinkDropdownIds?: string[];
  subLinks?: IBlink[];
  featureFlag?: string;
  shouldValidateMasterLink?: { func: (data?: any) => boolean };
  isNewBadge?: boolean;
  actionFlagValue?: string;
  callback?: () => void;
  disabledForRoles?: RoleType[];
  limitedFeatureType?: LimitedFeatureType;
  upgradeToUseFeature?: boolean;
}

export interface ICategory {
  description: string;
  id: string;
  imageName: string;
  taxMultiplier: number;
  title: string;
  transactionType: string;
}

export interface IExpenseObject {
  createdAt: string;
  customerId: string;
  endDate: string;
  externalId: string;
  path: string;
  startDate: string;
  title: string;
  viewed: number;
}

export interface IExpenseData {
  taxStatement: IExpenseObject[];
}

export interface ICategoryImageAndTitle {
  image: string;
  title: string;
}

export interface IUncategorizedTransactionsData {
  dates: string;
  externalId: string | undefined;
  quarter: string;
}

export interface ITransaction {
  checkDetailsData?: {
    checkAccountNumber?: string;
    checkNumber?: string;
    checkTransactionId?: string;
    enteredAmount?: string;
    status?: string;
    estimatedArrivalDate?: Date;
  };
  comment: string;
  categorizationType?: string;
  activityTypeCode: string;
  allowCategorizationEnum: string;
  allowComment: boolean;
  amount: number;
  billingAmount: number;
  billingCurrencyCode: string;
  trxTitle?: string;
  trxDescription?: string;
  categoryDetails: {
    receiptPath: string;
    business: boolean;
    categoryExternalId: string;
    businessPercent: number;
  };
  creditCard: true;
  currencyCode: string;
  details: string;
  localAmount: number;
  localCurrencyCode: string;
  locationDetails: { address: string; icon: string; phoneNumber: string };
  locationKey: string;
  mccCode: string;
  merchantName: string;
  onHold: boolean;
  pending: boolean;
  settleAmount: number;
  settleCurrencyCode: string;
  sourceId: string;
  suggestedTaxCategoryId: string;
  timestamp: string;
  transactionId: string;
  transactionStatus: string;
  trxType: string;
  trxTypeParams: string;
  type: string;
  // Enrichment types
  externalTransactionType?: string;
  description?: string;
  found?: boolean;
  prettyName?: string;
  sourceAchNumber?: string;
  sourceRoutingNumber?: string;
  targetAchNumber?: string;
  targetRoutingNumber?: string;
  title?: string;
  billerName?: string;

  [key: string]: any;

  expectedClearDate?: Date;
}

export interface ITransactionDetailsMap {
  TimeAndDate?: boolean;
  TransactionNameAndAmount?: boolean;
  TransactionTag?: boolean;
  BusinessPictureMap?: boolean;
  BusinessDetailsRow?: {
    address?: boolean;
    website?: boolean;
    phoneNo?: boolean;
  };
  TextArea?: boolean;
  TransactionReceipt?: boolean;
  NeedToVerify?: boolean;
  NeedToVerifyDisclaimer?: boolean;
  accountNumber?: boolean;
  routingNumber?: boolean;
  CheckPhoto?: boolean;
  CheckNumber?: boolean;
  EstimatedArrivalDate?: boolean;
}

export const transactionTypes = [
  undefined,
  'PURCHASE',
  'FUND_TRANSFER',
  'REFUND',
  'CHECK_DEPOSIT',
  'FEE',
  'ATM',
  'CACHE_DEPOSIT',
] as const;
export type TransactionType = (typeof transactionTypes)[number];

export interface ITransactionFilters {
  type?: { key: any; value: TransactionType | undefined };
  amount?: { min: number | string; max: number | string };
  date?: { min: Date | string; max: Date | string };
  page?: number;
  recordCnt?: number;
}

export interface IFilterDate {
  title: string;
  minDate: Date;
  maxDate: Date;
}

export interface ITableItems {
  date: string;
  name: string;
  status: string;
  amount: number;
  transactionId: string;
  trxType: string;
  trxTypeParams: string;
  externalTransactionType?: string;
  checkDetailsData: ITransaction['checkDetailsData'] | undefined;
}

export interface ITransactionsParams {
  accountNo: string | undefined;
  startDate: Date | string | undefined;
  endDate: Date | string | undefined;
  recordCnt: number;
  page: number;
  pending: boolean | undefined;
  amountFrom: number | undefined | string;
  amountTo: number | undefined | string;
  transactionFilterType: TransactionType | undefined;
}

export interface IAdditionalTransactionsStates {
  endOfTransactions: boolean;
  noTransactionsAtAll: boolean;
}

export interface IUncategorizedTransactions {
  endDate: string;
  numOfTransactions: number;
  page: number;
  startDate: string;
  transactionList: ITransaction[];
  uncategorizedTotalNumOfRecords: number;

  [key: string]: any;
}

export interface ILinkAccountProps {
  publicToken: string;
  institutionId: string;
  metaDataStr: string;
}

export interface ILinkedAccount {
  accountName: string;
  createTime: string;
  displayName: string;
  externalId: string;
  id: number;
  institutionId: string;
  institutionName: string;
  institutionLogo?: string;
  mask: string;
  officialName: string;
  updateTime: string;
  validForTransfer: string;
  routingNumber: string;
  accountId: string;
  accountHolderName: string;
  accountType?: string;
}

export interface ITrxData {
  trxType: string;
  externalTransactionType: string;
  trxTypeParams: string;
  transactionStatus: string;
  checkDetailsData: any;
}

export interface IPostLinkData {
  publicToken: string;
  metaData: PlaidLinkOnSuccessMetadata;
}

export interface IVendor {
  id: number;
  identifier: string;
  isVendorOpenForStandard: boolean;
  logoUrl: string;
  vendorName: string;
}

interface IActions {
  text: string;
  type: string;
  url: string;
}

export interface IVendorData {
  displayOptions: [
    {
      actions: IActions[];
      disclaimer: string;
      steps: string[];
      title: string;
      subtitle: string;
      type: string;
      vendorDescription: string;
      verificationDisplayType: string;
    },
  ];
  id: number;
  isInstantIntegrationAvailable: boolean;
  logoUrl: string;
  name: string;
}

export interface IAppCenterCategory {
  categoryId: number;
  vendorCategoryDescription: string;
  vendorCategoryName: string;
  vendors: IVendor[];
  vendorsAmount: number;
}

export interface ICustomer {
  externalId: string;
  phoneNumber: string;
  onboardingStatus: string;
  ssn?: string;
  idNumber?: string;
  address: {
    city: string;
    state: string;
    country: string;
    line1: string;
    line2: string;
    postalCode: string;
  };
  businessAddress: {
    city: string;
    state: string;
    country: string;
    line1: string;
    line2: string;
    postalCode: string;
  };
  accountAddress: {
    city: string;
    state: string;
    country: string;
    line1: string;
    line2: string;
    postalCode: string;
  };
  mailingAddress: {
    city: string;
    state: string;
    country: string;
    line1: string;
    line2: string;
    postalCode: string;
  };
  birthDate: string;
  firstName: string;
  lastName: string;
  email: string;
  lastLoginTime: string;
  createTime: string;
  securityQuestionSet: boolean;
  businessType: string;
  tcTime: string;
  loginType: string;
  loginName: string;
  mainIncomeSource: string;
  updatedBy: string;
  updatedByFlow: string;
  customerProfile: LiliPlanType;
  nameInitials: string;
}

export interface IPersonalAccount {
  accountExternalId: string;
  cards: ICard[];
  role?: string;

  [key: string]: any;
}

export enum OverdraftState {
  NOT_ELIGIBLE = 'NOT_ELIGIBLE',
  OPTED_OUT = 'OPTED_OUT',
  OPTED_IN = 'OPTED_IN',
  REVOKED = 'REVOKED',
}

export interface OverdraftDetails {
  eligible: boolean;
  optIn: boolean;
  eligibilityLimit: number;
  eligibilityUsed: number;
  daysUntilRenewal?: number;
  state: OverdraftState;
}

export interface IPrimaryAccount {
  balance: number;
  currencyCode: string;
  accountStatus: string;
  startDate: string;
  routingNumber: string;
  achAccountNumber: string;
  swiftCode?: string;
  wireRoutingNumber?: string;
  overdraft: OverdraftDetails;
  bankInfo: {
    bank: string;
    bankInstAddress: string;
    bankInstInfo: string;
    bankInstName: string;
  };
}

export interface IPrivateAccount extends IPersonalAccount, IPrimaryAccount {}

export interface ITaxBucketBalance {
  balance: number;
  available: boolean;
  active: boolean;
  savePercent: boolean;
  autoSave: boolean;
}

export interface IEmergencyBucketResponse {
  balance: number;
  available: boolean;
  active: boolean;
  currencyCode: string;
  amount: number;
  dayOfWeek: number;
  createTime: string;
  autoSave: boolean;
  accountId: string;
  accountType: string;
}

export interface IOtherBusiness {
  customerExternalId: string;
  name: string;
  ein: string;
  businessType: string;
  initials: string;
}

export enum TeamMemberInviteStatus {
  ACTIVE = 'ACTIVE',
  COMPLETED = 'COMPLETED',
}

export enum AccessLevel {
  FULL_ACCESS = 'FULL_ACCESS',
  LIMITED_ACCESS = 'LIMITED_ACCESS',
}

export interface TeamMember {
  initials: string;
  firstName: string;
  lastName: string;
  email: string;
  externalId: string;
  isAllowedForRemoval: boolean;
  isCurrentlyLoggedInUser: boolean;
  teamMemberType: RoleType;
  invitationStatus: TeamMemberInviteStatus;
  permissionsAccessLevel: AccessLevel;
}

export interface IAccountant {
  name: string;
  email: string;
  inviteStatus: string;
  linkStatus: string;
  externalId: string;
  phoneNumber: string;
}

export interface FeatureAccessItem {
  name: string;
  role: RoleType;
  permission: FeatureAccess;
  localizedName: string;
}

interface AuthorizedUserDetails {
  email: string;
  phoneNumber: string;
  firstName: string;
  lastName: string;
  type: RoleType;
}

interface AuthorizedUserInvite {
  customerExternalId: string;
  status: InviteStatus;
  firstName: string;
  lastName: string;
  ssn: string;
  businessName: string;
  ein: string;
  expirationDate: string;
}

interface AuthorizedUserData {
  authorizedUserDetails?: AuthorizedUserDetails;
  authorizedUserFeatureItems: FeatureAccessItem[];
  authorizedUserInvites: AuthorizedUserInvite[];
  authorizedUserPendingInvites: AuthorizedUserInvite[];
  externalId: string;
}

export interface ISummaryData {
  currentTime: string;
  accountant: IAccountant;
  customer: ICustomer;
  accounts: IPrivateAccount[];
  authorizedUserData?: AuthorizedUserData;
  personExternalId: string;
  primaryAccount: IPrivateAccount;
  otherBusinesses: IOtherBusiness[];
  securityQuestionExists: boolean;
  authorizedUserType?: RoleType;
  taxBucketBalance: {
    balance: number;
    available: boolean;
    active: boolean;
    savePercent: boolean;
    autoSave: boolean;
  };
  emergencyBucketResponse: {
    balance: number;
    available: boolean;
    active: boolean;
    currencyCode: string;
    amount: number;
    dayOfWeek: number;
    createTime: string;
    autoSave: boolean;
    accountId: string;
    accountType: string;
  };
  referralWaitForPrize: boolean;
  actionFlags: {
    directDeposit: boolean;
    accountFunded: boolean;
    requestPayment: boolean;
    firstCard: boolean;
    depositCheckAccepted: boolean;
    fiveDepositsOrMore: boolean;
    directDepositEmailUsed: boolean;
    madeSwipe: boolean;
    instantDepositMaximumUsed: boolean;
    instantDepositAvailable: boolean;
    walletActive: boolean;
    allowViewMyCard: boolean;
    createInvoice: boolean;
    allowInvoiceAchPay: boolean;
    hasDisputes: boolean;
    businessAddressDisplay: boolean;
    businessAddressEnabled: boolean;
    businessInProgress: boolean;
    allowAdditionalBusiness: boolean;
    finishedInternationalWireEdd: boolean;
    needUpdateNaicsCode: boolean
    needUpdateBusinessAddressForRegisterAgentAddress: boolean
  };
  unreadStatements: number;
  unreadTaxStatements: number;
  customerProfileInfo: {
    customerProfile: LiliPlanType;
    profileFee: number;
    profileFeeNextDueDate: string;
    annual: boolean;
  };
  dba: {
    status: string;
    name?: string;
  };
  business: {
    name?: string;
    status: string;
  };
  checkingTotalBalance: number;
  savingsTotalBalance: number;
  teamMembers: TeamMember[];
}

export interface IScheduleC {
  scheduleCId: string;
  customerId: string;
  fileCreated: boolean;
  scheduleCTitle: string;
  year: number;
}

export interface IScheduleCData {
  scheduleCs: IScheduleC[];
}

export interface IIncomeData {
  totalIncome: number;
  totalCharityDonations: number;
}

export interface ICategoryDetails {
  business: boolean;
  businessPercent: number;
  categoryExternalId: string;
  receiptPath: string;
}

export type ContextType = typeof ContextTypes.ACH_OUT | typeof ContextTypes.BILL_PAY;

export interface IFeeItem {
  achTransferType: string;
  arrivalDate: string;
  arrivalDateShort: string;
  fee: number;
}

export interface IBusiness {
  businessType: string;
  customerExternalId: string;
  ein: string;
  initials: string;
  name: string;
}

export interface IInvoiceReminder {
  invoiceReminderType: string;
  numberOfDays: number;
  reminderText: string;
  selected: boolean;
}

export interface IInvoiceServiceItem {
  amount: number;
  description: string;
}

export interface IInvoice {
  brandColor: string;
  createTime: string;
  externalId: string;
  invoiceNumber: string;
  invoicePayment: any[];
  invoicePaymentTerm: {
    achPaymentMethod: boolean;
    checkDepositPaymentMethod: boolean;
    dueDate: string;
    dueDateDescription: string;
  };
  invoiceRecipient: {
    email: string;
    externalId: string;
    name: string;
  };
  invoiceReminders: IInvoiceReminder[];
  invoiceService: {
    amount: number;
    description: string;
    invoiceServiceItems: IInvoiceServiceItem[];
  };
  invoiceStatus: string;
  issueDate: string;
  payViaOptions: any[];
  paymentUrl: string;
  sentEmails: any[];
  templateId: string;
  templateVersion: number;
}

export interface IBill {
  amount: number;
  billId: string;
  billNumber: string;
  contentType: string;
  createTime: string;
  currencyCode: string;
  documentId: string;
  dueDate: string;
  supplierId: string;
  supplierName: string;
  updateTime: string;
  calculatedStatus: string;
  isSupportRequestVendorDetails: boolean;
  recurringPaymentData: IRecurringPaymentData;
}

export enum GENERIC_FILE_UPLOAD_TYPES {
  PLAN_UPGRADE_SUCCESS = 'plan_upgrade_success',
  CHANGE_PASSWORD = 'change_password',
  TAX_BUCKET_AMOUNT = 'tax_bucket_amount',
  TAX_BUCKET_SETUP = 'tax_bucket_setup',
  WIRE_OUT = 'wire_out',
  INTL_WIRE_OUT = 'international_wire_out',
  MAIL_A_CHECK = 'mail_a_check',
  SAVINGS_ACCOUNT = 'savings_account',
  ASK_VENDOR_FOR_DETAILS = 'ask_vendor_details_confirmation',
  ACH_OUT = 'ach_out',
  LIMIT_INCREASE_REQUEST = 'LIMIT_INCREASE_REQUEST',
}

export enum BANK_DEFINITION_TYPES {
  CHOICE = 'CHOICE',
  SUNRISE = 'SUNRISE',
  CHOICE_TO_SUNRISE = 'CHOICE_TO_SUNRISE',
}
